// Ekran 14 · "Eşik & Tolerans" sekmesi.
//
// Referans tablo dört kolonu (Normal min/maks · Amber · Kırmızı) her satırda
// AYNI anlamla kullanmıyordu: OLR satırında §6.3'ün kritik değeri (4,5) amber
// kutusuna yazılmış ve kırmızı kolonu kapatılmıştı; HRT'de de aynı kayma vardı.
// Harfiyen kopyalansaydı "Kaydet" düğmesi sessizce bant bozan bir tuş olurdu.
//
// Çözüm: sunucu her satırın KALIBINI söylüyor (üst sınırlı · aralık-üstte-kırmızı
// · aralık-altta-kırmızı · aralık · tolerans); ekran yalnız o kalıbın anlamlı
// kolonlarını açıyor, diğerlerini "—" ile kapalı gösteriyor ve satırın nasıl
// okunacağını yazıyor. Doğrulama da kalıba göre sunucuda.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { trGiris, sayiyaCevirBos } from '../../lib/trFormat'
import { ayarlar as ayarApi, type EsikSatiri, type EsikYaz } from './api'

type Kolon = 'normalMin' | 'normalMax' | 'amber' | 'kirmizi'

const KOLONLAR: { anahtar: Kolon; baslik: string }[] = [
  { anahtar: 'normalMin', baslik: 'Normal (min)' },
  { anahtar: 'normalMax', baslik: 'Normal (maks)' },
  { anahtar: 'amber', baslik: 'Amber sınırı' },
  { anahtar: 'kirmizi', baslik: 'Kırmızı sınırı' },
]

const KALIP_ETIKET: Record<string, string> = {
  ustSinir: 'üst sınırlı',
  aralikUstteKirmizi: 'aralık · risk üstte',
  aralikAlttaKirmizi: 'aralık · risk altta',
  aralik: 'aralık',
  tolerans: 'tolerans',
}

type Taslak = Record<string, Record<Kolon, string>>

function taslakKur(satirlar: EsikSatiri[]): Taslak {
  const t: Taslak = {}
  for (const s of satirlar) {
    t[s.gosterge] = {
      normalMin: trGiris(s.normalMin),
      normalMax: trGiris(s.normalMax),
      amber: trGiris(s.amber),
      kirmizi: trGiris(s.kirmizi),
    }
  }
  return t
}

export default function EsikTablosu() {
  const kuyruk = useQueryClient()
  const sorgu = useQuery({ queryKey: ['esikler'], queryFn: ayarApi.esikler })
  const [taslak, setTaslak] = useState<Taslak>({})

  useEffect(() => {
    if (sorgu.data) setTaslak(taslakKur(sorgu.data))
  }, [sorgu.data])

  const kaydet = useMutation({
    mutationFn: (govde: EsikYaz[]) => ayarApi.esikleriYaz(govde),
    onSuccess: async (yeni) => {
      kuyruk.setQueryData(['esikler'], yeni)
      // Eşik değişince BÜTÜN bantlı görünümler bayatlar (§8-14).
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['panel'] }),
        kuyruk.invalidateQueries({ queryKey: ['beyan'] }),
        kuyruk.invalidateQueries({ queryKey: ['besleme'] }),
        kuyruk.invalidateQueries({ queryKey: ['dolumlar'] }),
        kuyruk.invalidateQueries({ queryKey: ['evraklar'] }),
      ])
      toast('Eşikler kaydedildi — göstergeler yeni bantlarla yeniden hesaplandı')
    },
    onError: (h) =>
      toast(
        h instanceof ApiHatasi && h.detay?.length
          ? `${h.message} — ${h.detay[0]}`
          : h instanceof Error
            ? h.message
            : 'Eşikler kaydedilemedi.',
      ),
  })

  const satirlar = sorgu.data ?? []

  const degisenler = useMemo<EsikYaz[]>(() => {
    const liste: EsikYaz[] = []
    for (const s of satirlar) {
      const t = taslak[s.gosterge]
      if (!t) continue
      const yeni = {
        gosterge: s.gosterge,
        normalMin: sayiyaCevirBos(t.normalMin),
        normalMax: sayiyaCevirBos(t.normalMax),
        amber: sayiyaCevirBos(t.amber),
        kirmizi: sayiyaCevirBos(t.kirmizi),
      }
      const ayni =
        yeni.normalMin === s.normalMin &&
        yeni.normalMax === s.normalMax &&
        yeni.amber === s.amber &&
        yeni.kirmizi === s.kirmizi
      if (!ayni) liste.push(yeni)
    }
    return liste
  }, [satirlar, taslak])

  function yaz(gosterge: string, kolon: Kolon, deger: string) {
    setTaslak((t) => ({ ...t, [gosterge]: { ...t[gosterge], [kolon]: deger } }))
  }

  if (sorgu.isLoading) return <div className="card"><div className="insp-empty">Eşikler yükleniyor…</div></div>
  if (sorgu.isError) return <div className="card"><div className="insp-empty">Eşikler alınamadı.</div></div>

  return (
    <div className="card">
      <div className="sect-t">
        Eşik &amp; Tolerans Sözlüğü
        <span className="cell-sub" style={{ marginLeft: 6 }}>
          §6.3 — tesis bazında düzenlenebilir
        </span>
      </div>

      <table className="grid esik-t">
        <thead>
          <tr>
            <th>Gösterge</th>
            {KOLONLAR.map((k) => (
              <th className="num" key={k.anahtar}>
                {k.baslik}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr key={s.gosterge}>
              <td>
                <div className="cell-name">{s.etiket}</div>
                <div className="cell-sub">
                  {s.birim ? `${s.birim} · ` : ''}
                  {KALIP_ETIKET[s.kalip] ?? s.kalip} · {s.kullanildigiYer}
                </div>
                <div className="cell-sub" style={{ color: 'var(--muted)', marginTop: 2 }}>
                  {s.nasilOkunur}
                </div>
              </td>
              {KOLONLAR.map((k) => {
                const acik = s.kolonlar.includes(k.anahtar)
                return (
                  <td className="num" key={k.anahtar}>
                    <input
                      value={acik ? (taslak[s.gosterge]?.[k.anahtar] ?? '') : '—'}
                      disabled={!acik}
                      aria-label={`${s.etiket} · ${k.baslik}`}
                      title={
                        acik
                          ? undefined
                          : 'Bu gösterge için bu kolon kullanılmıyor — motor okumuyor.'
                      }
                      onChange={(e) => yaz(s.gosterge, k.anahtar, e.target.value)}
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid-foot">
        {/* Referansın kalıcı cümlesi + kaydedilmemiş satır sayacı: cümle
            kaydetmenin NEYİ etkilediğini, sayaç ise düğmenin niye kapalı
            olduğunu söylüyor. */}
        <span>
          Değişiklik tüm gösterge bantlarını, uyarı motorunu ve kapanış ışıklarını
          anında etkiler
          {degisenler.length > 0 &&
            ` · ${degisenler.length} satır değişti — kaydedilmedi`}
        </span>
        <button
          className="btn btn-pri btn-sm"
          type="button"
          disabled={degisenler.length === 0 || kaydet.isPending}
          onClick={() => kaydet.mutate(degisenler)}
        >
          Eşikleri kaydet
        </button>
      </div>

      {/* Referansta olmayan dipnot: kolon anlamı satırdan satıra değiştiği için
          (bkz. kararlar-ek "Eşik tablosunun sütun anlamı") kapalı hücrelerin ne
          demek olduğu yazılmazsa tablo yanlış okunur. Kaydetmenin etkisi artık
          .grid-foot'ta yazdığı için burada tekrarlanmıyor. */}
      <div className="pnote">
        Kolonların anlamı göstergeye göre değişir; kapalı hücreler o satırda
        <b> motor tarafından okunmuyor</b> demektir.
      </div>
    </div>
  )
}
